import React, { createContext, useContext, useState, useCallback } from 'react';
import { streaming } from '../services/api';
import WebRTC from '../services/webrtc';

const StreamContext = createContext();

export const useStream = () => {
  const context = useContext(StreamContext);
  if (!context) {
    throw new Error('useStream must be used within a StreamProvider');
  }
  return context;
};

export const StreamProvider = ({ children }) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const startStream = useCallback(async (gameId) => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await streaming.startSession(gameId);
      const webrtc = new WebRTC(session.data.connectionDetails);
      
      await webrtc.connect();
      
      setStream({
        sessionId: session.data.sessionId,
        stream: webrtc.getStream(),
        sendInput: (input) => webrtc.sendInput(input)
      });
      
      // Start heartbeat
      const heartbeatInterval = setInterval(() => {
        streaming.heartbeat(session.data.sessionId).catch(console.error);
      }, 30000);
      
      return () => clearInterval(heartbeatInterval);
    } catch (err) {
      setError(err.message || 'Failed to start streaming session');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const stopStream = useCallback(async () => {
    if (!stream?.sessionId) return;
    
    try {
      await streaming.endSession(stream.sessionId);
      setStream(null);
    } catch (err) {
      setError(err.message || 'Failed to stop streaming session');
      throw err;
    }
  }, [stream]);

  return (
    <StreamContext.Provider
      value={{
        stream,
        error,
        isLoading,
        startStream,
        stopStream
      }}
    >
      {children}
    </StreamContext.Provider>
  );
};

export default StreamContext;